import { useEffect, useState } from 'react';
import type { LoanFinancialSnapshotV1 } from './loan-financial.types';
import { formatMoneyAr } from './loan-financial.format';
import { MoneyRow, SectionCard } from './LoanUiBits';
import { apiService } from '../../services/api.service';

type Props = {
  loanId: string;
  snap: LoanFinancialSnapshotV1;
  canView: boolean;
  canPost: boolean;
  canWriteOff: boolean;
  canRecover: boolean;
  onRefresh: () => Promise<void> | void;
};

/**
 * Panel 4L — Riesgo / Incobrabilidad. Provision OFF por default (DEC-11).
 */
export function LoanCreditRiskPanel({
  loanId,
  snap,
  canView,
  canPost,
  canWriteOff,
  canRecover,
  onRefresh,
}: Props) {
  const [summary, setSummary] = useState<any>(null);
  const [preview, setPreview] = useState<any>(null);
  const [woPreview, setWoPreview] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [recoveryAmount, setRecoveryAmount] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!canView) return;
    try {
      const s = await apiService.getLoanCreditRisk(loanId);
      setSummary(s);
    } catch {
      setSummary(null);
    }
  };

  useEffect(() => {
    void load();
  }, [loanId, canView]);

  if (!canView) return null;

  const status = snap.creditRisk?.status ?? summary?.creditRiskStatus ?? null;
  const writtenOff = status === 'WRITTEN_OFF';
  /** 5B — DEC-11: sin policy enabled no hay acciones productivas. */
  const actionsEnabled = Boolean(summary?.actionsEnabled ?? summary?.provisionEnabled);
  const allowPost = canPost && actionsEnabled;
  const allowWriteOff = canWriteOff && actionsEnabled;
  const allowRecover = canRecover && writtenOff;

  return (
    <SectionCard
      title="Riesgo / Incobrabilidad"
      subtitle="Previsión y castigo son contables. No reducen la deuda contractual del cliente. DEC-11: policy OFF por default."
    >
      {status === 'PROVISIONED' ? (
        <p className="mb-2 text-xs font-medium text-amber-800">PREVISIONADO (riesgo contable)</p>
      ) : null}
      {writtenOff ? (
        <p className="mb-2 text-xs font-medium text-rose-800">CASTIGADO CONTABLEMENTE</p>
      ) : null}

      <MoneyRow label="Gross CxC" value={summary?.grossReceivable ?? snap.accounting?.grossReceivable ?? snap.accounting?.totalReceivable} />
      <MoneyRow label="Allowance" value={summary?.allowance ?? snap.accounting?.allowance ?? 0} />
      <MoneyRow
        label="Net CxC"
        value={summary?.netReceivable ?? snap.accounting?.netReceivable}
        emphasis
      />
      {writtenOff ? (
        <>
          <MoneyRow label="Castigado" value={summary?.writtenOffAmount ?? 0} />
          <MoneyRow label="Recuperado" value={summary?.recoveredAmount ?? 0} />
          <MoneyRow label="Pendiente recupero" value={summary?.remainingRecoverable ?? 0} />
        </>
      ) : null}

      {!actionsEnabled ? (
        <p className="mb-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          ProvisionPolicy deshabilitada (DEC-11). Previsión / castigo / recupero no disponibles en
          producción controlada.
        </p>
      ) : null}

      {!writtenOff && allowPost ? (
        <div className="mt-3 space-y-2 border-t border-slate-200 pt-3">
          <button
            type="button"
            className="rounded bg-slate-700 px-3 py-1.5 text-sm text-white disabled:opacity-50"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                const p = await apiService.previewLoanProvision(loanId);
                setPreview(p);
              } catch (e: any) {
                setError(e?.response?.data?.message ?? e?.message ?? 'Preview falló (¿policy OFF?)');
                setPreview(null);
              } finally {
                setBusy(false);
              }
            }}
          >
            Preview previsión
          </button>
          {preview ? (
            <div className="text-sm text-slate-700">
              <p>
                Requerida {formatMoneyAr(preview.requiredProvision)} · Contabilizada{' '}
                {formatMoneyAr(preview.currentAllowance)} · Delta {formatMoneyAr(preview.delta)}
              </p>
              <button
                type="button"
                className="mt-2 rounded bg-amber-700 px-3 py-1.5 text-sm text-white"
                disabled={busy || Math.abs(preview.delta) < 0.01}
                onClick={async () => {
                  setBusy(true);
                  setError(null);
                  try {
                    await apiService.postLoanProvision(loanId, { asOf: preview.asOf });
                    setPreview(null);
                    await onRefresh();
                    await load();
                  } catch (e: any) {
                    setError(e?.response?.data?.message ?? e?.message);
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Contabilizar ajuste
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {!writtenOff && allowWriteOff ? (
        <div className="mt-3 space-y-2 border-t border-slate-200 pt-3">
          <p className="text-xs text-slate-500">
            El castigo da de baja la CxC contable. No elimina el historial del préstamo.
          </p>
          <button
            type="button"
            className="rounded border border-rose-300 px-3 py-1.5 text-sm text-rose-800"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                setWoPreview(await apiService.previewLoanWriteOff(loanId));
              } catch (e: any) {
                setError(e?.response?.data?.message ?? e?.message);
              } finally {
                setBusy(false);
              }
            }}
          >
            Preview castigo
          </button>
          {woPreview ? (
            <div className="space-y-2 text-sm">
              <MoneyRow label="Gross a castigar" value={woPreview.gross} />
              <MoneyRow label="Allowance usada" value={woPreview.allowanceUsed} />
              <MoneyRow label="Gasto adicional" value={woPreview.expenseRecognized} />
              <input
                className="w-full rounded border px-2 py-1"
                placeholder="Motivo (obligatorio)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <button
                type="button"
                className="rounded bg-rose-700 px-3 py-1.5 text-white disabled:opacity-50"
                disabled={busy || !reason.trim()}
                onClick={async () => {
                  setBusy(true);
                  setError(null);
                  try {
                    await apiService.postLoanWriteOff(loanId, { reason });
                    setWoPreview(null);
                    setReason('');
                    await onRefresh();
                    await load();
                  } catch (e: any) {
                    setError(e?.response?.data?.message ?? e?.message);
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Confirmar castigo contable
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {writtenOff && allowRecover ? (
        <div className="mt-3 space-y-2 border-t border-slate-200 pt-3">
          <p className="text-sm font-medium text-slate-800">Registrar recupero</p>
          <input
            className="w-full rounded border px-2 py-1"
            type="number"
            placeholder="Monto"
            value={recoveryAmount}
            onChange={(e) => setRecoveryAmount(e.target.value)}
          />
          <button
            type="button"
            className="rounded bg-emerald-700 px-3 py-1.5 text-sm text-white disabled:opacity-50"
            disabled={busy || !(Number(recoveryAmount) > 0)}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await apiService.postLoanRecovery(loanId, {
                  amount: Number(recoveryAmount),
                });
                setRecoveryAmount('');
                await onRefresh();
                await load();
              } catch (e: any) {
                setError(e?.response?.data?.message ?? e?.message);
              } finally {
                setBusy(false);
              }
            }}
          >
            Confirmar recupero
          </button>
        </div>
      ) : null}

      {error ? <p className="mt-2 text-sm text-red-600">{error}</p> : null}
    </SectionCard>
  );
}
